import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLockup } from "@/components/Brand";

export default function SetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();
  const [info, setInfo] = useState(undefined); // undefined=loading
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo({ valid: false, notFound: true }));
  }, [token]);

  const submit = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don’t match");
    setSaving(true);
    try {
      await acceptInvite(token, pw, name.trim() || undefined);
      toast.success("Welcome to Playbook Data");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const invalid = info && !info.valid;
  const reason = info?.notFound ? "This invite link is invalid." : info?.accepted ? "This invite has already been used." : info?.revoked ? "This invite was revoked." : info?.expired ? "This invite has expired." : "This invite link is no longer valid.";

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><BrandLockup /></div>
        <div className="pb-card p-6 sm:p-8">
          {info === undefined && <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>}

          {invalid && (
            <div data-testid="invite-invalid" className="flex flex-col items-center gap-3 py-6 text-center">
              <XCircle className="h-10 w-10 text-red-500" />
              <p className="font-medium">{reason}</p>
              <Button variant="outline" className="rounded-full" onClick={() => navigate("/login")}>Go to sign in</Button>
            </div>
          )}

          {info && info.valid && (
            <>
              <h1 className="font-display text-2xl font-extrabold">Set your password</h1>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-neutral-500"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Invited as <strong className="text-neutral-700">{info.email}</strong></p>
              <p className="mt-1 text-xs text-neutral-400">You choose your own password — nobody else can see or set it.</p>
              <div className="mt-5 grid gap-4">
                <div className="grid gap-1.5">
                  <Label>Your name (optional)</Label>
                  <Input data-testid="sp-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Albert" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Password</Label>
                  <Input data-testid="sp-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Confirm password</Label>
                  <Input data-testid="sp-password2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
                </div>
                <Button data-testid="sp-submit" onClick={submit} disabled={saving} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1.5 h-4 w-4" />} Create account & sign in
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
